import { Directive, HostBinding, HostListener, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TAdPlacement } from '@x/common/ads/ad.config';
import { WindowRef } from '@x/common/browser';
import { TrackingService } from '@x/common/tracking';
import { TEClickShopAd } from '@x/common/tracking/event-models';
import { TShopAd } from '@x/ecommerce/shop-client';

@Directive({
  selector: '[xAdClick]',
  standalone: true,
})
export class AdClickDirective {
  @Input({ required: true, alias: 'xAdClick' }) set url(ad: TShopAd | undefined) {
    if (!ad) return;

    this.ad = ad;

    try {
      this.parsedUrl = new URL(ad.url);
      this.relativeUrl = null;
    } catch (e) {
      const _relativeUrl = this.validRelativeUrl(ad.url);

      if (_relativeUrl && this.newTab) {
        this.parsedUrl = new URL(this.windowRef.instance.location.origin + _relativeUrl);
        this.relativeUrl = null;

        return;
      }

      this.relativeUrl = _relativeUrl;
      this.parsedUrl = null;
    }
  }
  @Input({ required: true }) placement: TAdPlacement;

  @Input() taxonId?: number;

  @HostBinding('class')
  class: string = 'cursor-pointer';

  get newTab(): boolean {
    return this.ad.urlTarget === '_blank';
  }

  private parsedUrl: URL | null = null;
  private relativeUrl: string | null = null;
  private ad: TShopAd;

  get channelCode(): string | undefined | null {
    return this.route.snapshot.params.channelCode;
  }

  @HostListener('click', ['$event'])
  onClick(event: Event) {
    event.preventDefault();
    this.navigate();
    this.trackEvent();
  }

  constructor(
    private windowRef: WindowRef,
    private router: Router,
    private trackingService: TrackingService,
    private route: ActivatedRoute,
  ) {}

  navigate() {
    if (this.parsedUrl) {
      const finalUrl = this.parsedUrl.href + this.formatQueryParams(this.parsedUrl.search);
      this.windowRef.instance.open(finalUrl, this.newTab ? '_blank' : '_self');
      return;
    }

    if (this.relativeUrl) {
      this.router.navigate([this.relativeUrl], {
        queryParamsHandling: 'merge',
        queryParams: { shopAdId: this.ad.id, shopAdPlacement: this.placement },
      });
      return;
    }

    console.error('Invalid URL');
  }

  trackEvent() {
    if (!this.parsedUrl && !this.relativeUrl) return;

    const taxonId = this.taxonId ?? Number(this.route.snapshot.params.taxonId);

    this.trackingService.sendEvent(
      new TEClickShopAd(this.trackingService.routeOrigin, {
        shopAdId: this.ad.id,
        placement: this.placement,
        taxonId,
      }),
    );
  }

  private formatQueryParams(existingParams: string): string {
    const urlParams = new URLSearchParams(existingParams);
    urlParams.set('shopAdId', this.ad.id.toString());
    urlParams.set('shopAdPlacement', this.placement.toString());
    return `?${urlParams.toString()}`;
  }

  private validRelativeUrl(url: string): string | null {
    if (!this.channelCode) return null;

    // Ensure the URL starts with a '/'
    if (!url.startsWith('/')) url = '/' + url;

    // add channel if not present
    if (!url.startsWith(`/${this.channelCode}`)) {
      url = `/${this.channelCode}${url}`;
    }

    const relativeUrlPattern = /^\/[a-zA-Z0-9-._~%!$&'()*+,;=:@/]*$/;
    return relativeUrlPattern.test(url) ? url : null;
  }
}
